// src/components/calendar/CalendarViews/MonthView.jsx - MINIMAL FIX: Only remove useEvents
import React from 'react'; // ✅ REMOVED useEffect import
import { Spinner } from '../../ui/spinner';

// ✅ KEEP: Import our shadcn month components
import MonthHeaderRow from '../../ui/calendar/month/MonthHeaderRow/MonthHeaderRow';
import MonthViewDateCell from '../../ui/calendar/month/MonthViewDateCell/MonthViewDateCell';
import MonthViewEventsContainer from '../../ui/calendar/month/MonthViewEventsContainer/MonthViewEventsContainer';
import MonthViewEventCard from '../../ui/calendar/month/MonthViewEventCard/MonthViewEventCard';

const MonthView = ({
  currentDate,
  previewEvent,     // ✅ KEEP: Preview still managed by parent
  onTimeSlotClick,
  onEventClick,
  isAdmin,
  showEventPanel,

  // ✅ NEW: Accept events data as props instead of useEvents hook
  events = [],      // Renamed from calendarEvents for clarity
  loading = false,  // Loading state from parent
  error = null,      // Error state from parent

  // creation mode props
  isInCreationMode = false,

  availabilityDates,
  viewingPartnerAvailability,

  // Block mode props
  isBlockMode = false,
  persistedSlots = [],
  onSlotsChanged,

  // Selected event for detail panel
  selectedEventId = null
}) => {

  // ✅ KEEP: Date calculation logic unchanged (pure function)
  const getMonthDates = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstWeekStart = new Date(start);
    firstWeekStart.setDate(start.getDate() - start.getDay());

    const dates = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(firstWeekStart);
      date.setDate(firstWeekStart.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  // ✅ UPDATED: Use passed events instead of hook method
  const getEventsForDate = (dateStr) => {
    return events.filter(event => {
      if (!event.start) return false;
      const eventDateStr = event.start.toISOString().split('T')[0];
      return eventDateStr === dateStr;
    });
  };

  // ✅ UPDATED: Use passed events instead of hook method
  const getAllEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const eventsForDate = getEventsForDate(dateStr);
    const allEvents = eventsForDate;

    return allEvents.filter(event => {
      const eventDate = event.start.toDateString();
      const targetDate = date.toDateString();
      return eventDate === targetDate;
    });
  };

  // ✅ KEEP: Visible events calculation (IMPORTANT FUNCTION)
  const getVisibleEventsForDate = (date) => {
    const allEvents = getAllEventsForDate(date);
    const MAX_VISIBLE_EVENTS = 3;

    return allEvents.slice(0, MAX_VISIBLE_EVENTS);
  };

  // ✅ KEEP: Overflow calculation (IMPORTANT FUNCTION)
  const getOverflowInfo = (date) => {
    const allEvents = getAllEventsForDate(date);
    const MAX_VISIBLE_EVENTS = 3;

    const overflowCount = Math.max(0, allEvents.length - MAX_VISIBLE_EVENTS);

    return {
      hasOverflow: overflowCount > 0,
      overflowCount,
      totalEvents: allEvents.length
    };
  };

  const handleOverflowClick = (date, overflowInfo) => {
    // During creation mode, clicking overflow should move preview to 9 AM
    if (isInCreationMode) {
      onTimeSlotClick(date, 9);  // Use unified handler with hour 9
      return;
    }

    console.log('Month overflow clicked:', date, overflowInfo);
    // Could open a popup or expand the cell to show all events
  };

  // Block mode: toggle all-day block for clicked date
  const handleMonthCellClick = (date, hour) => {
    if (isBlockMode && onSlotsChanged) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const idx = persistedSlots.findIndex(s => s.dateStr === dateStr && s.allDay);
      const updated = idx !== -1
        ? persistedSlots.filter((_, i) => i !== idx)
        : [...persistedSlots, { dateStr, allDay: true }];
      onSlotsChanged(updated);
      return;
    }
    onTimeSlotClick(date, hour);
  };

  const monthDates = getMonthDates(currentDate);
  const today = new Date().toDateString();

  // ✅ UPDATED: Use passed loading state instead of hook
  if (loading && !events.length) {
    return (
      <div className="month-view flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Spinner className="size-8 text-blue-500 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Loading calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ UPDATED: Use passed error state instead of hook
  if (error) {
    return (
      <div className="month-view flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-400 text-sm">Error loading calendar: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-3 py-1 bg-slate-700 text-slate-300 rounded text-sm hover:bg-slate-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ KEEP: Complete JSX using shadcn components with all original functionality
  return (
    <div className="month-view flex flex-col h-full">
      {/* ✅ KEEP: Month header using shadcn component */}
      <MonthHeaderRow />

      {/* ✅ KEEP: Calendar grid using shadcn components with full functionality */}
      <div className="flex-1 grid grid-cols-7 gap-0">
        {monthDates.map((date, index) => {
          const isToday = date.toDateString() === today;
          const isSelected = date.toDateString() === currentDate.toDateString();
          const visibleEvents = getVisibleEventsForDate(date);
          const overflowInfo = getOverflowInfo(date);

          const cellDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const isBlockSelected = isBlockMode &&
            persistedSlots.some(s => s.dateStr === cellDateStr && s.allDay);

          return (
            <MonthViewDateCell
              key={index}
              date={date}
              currentDate={currentDate}
              isToday={isToday}
              isSelected={isSelected}
              overflowInfo={overflowInfo}
              onOverflowClick={handleOverflowClick}
              isClickable={isAdmin || isBlockMode}
              onClick={handleMonthCellClick}
              hasAvailability={viewingPartnerAvailability && availabilityDates && availabilityDates.has(date.toISOString().split('T')[0])}
              viewingPartnerAvailability={viewingPartnerAvailability}
              isBlockSelected={isBlockSelected}
            >
              {/* ✅ KEEP: Events container with individual event cards using shadcn components */}
              <MonthViewEventsContainer>
                {visibleEvents.map((event, eventIndex) => (
                  <MonthViewEventCard
                    key={eventIndex}
                    event={event}
                    onClick={onEventClick}
                    showEventPanel={showEventPanel}
                    isSelected={selectedEventId === event.id}
                  />
                ))}
              </MonthViewEventsContainer>
            </MonthViewDateCell>
          );
        })}
      </div>
    </div>
  );
};

export default MonthView;